/**
 * Comptes rendus du technicien + formulaire de création.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  createReport,
  listTechnicianProjects,
  listTechnicianReports,
} from '@/features/admin/admin-api';
import { extractErrorMessage } from '@/features/auth/error-message';

export function TechnicianReportsPage() {
  const qc = useQueryClient();
  const reports = useQuery({
    queryKey: ['technician', 'reports'],
    queryFn: listTechnicianReports,
  });
  const projects = useQuery({
    queryKey: ['technician', 'projects'],
    queryFn: listTechnicianProjects,
  });

  const [form, setForm] = useState({ projectId: '', title: '', content: '' });

  const createMut = useMutation({
    mutationFn: () =>
      createReport({
        projectId: form.projectId,
        title: form.title,
        content: form.content,
      }),
    onSuccess: () => {
      toast.success('Compte rendu enregistré');
      qc.invalidateQueries({ queryKey: ['technician', 'reports'] });
      qc.invalidateQueries({ queryKey: ['technician', 'projects'] });
      setForm({ projectId: '', title: '', content: '' });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <DashboardLayout variant="technicien" title="Comptes rendus">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Mes comptes rendus</CardTitle>
            <CardDescription>
              {reports.data ? `${reports.data.length} rapports` : 'Chargement…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {reports.data?.length ? (
              <ul className="space-y-3">
                {reports.data.map((r) => (
                  <li key={r.id} className="rounded-md border bg-white p-3">
                    <p className="font-medium">{r.title}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Projet {r.projectId} ·{' '}
                      {new Date(r.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
                      {r.content}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Aucun compte rendu pour le moment.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Nouveau compte rendu</CardTitle>
            <CardDescription>
              Rédigez le compte rendu d&apos;une intervention récente.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="projectId">Dossier</Label>
              <select
                id="projectId"
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Sélectionner…</option>
                {projects.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.reference}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="title">Titre</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="content">Contenu</Label>
              <textarea
                id="content"
                rows={6}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <Button
              className="w-full"
              disabled={
                !form.projectId || !form.title || !form.content || createMut.isPending
              }
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? 'Envoi…' : 'Enregistrer'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
